import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { SocialShareSettings } from 'src/interfaces/SocialShare';

const emptyLocale = () => ({
  title: '',
  description: '',
});

export const emptySocialShareSettings = (): SocialShareSettings => ({
  key: 'site-default',
  imageMode: 'upload',
  imageUrl: '/uploads/og/cover.png',
  imageWidth: 1200,
  imageHeight: 630,
  backgroundColor: '#9C5A37',
  siteName: 'Teleki Trail',
  locales: {
    en: emptyLocale(),
    hu: emptyLocale(),
    ro: emptyLocale(),
  },
  composedDocument: { blocks: [] },
});

interface SocialShareState {
  settings: SocialShareSettings | null;
  isLoading: boolean;
  fetchSettings: () => Promise<SocialShareSettings | null>;
  saveSettings: (settings: SocialShareSettings) => Promise<SocialShareSettings>;
}

const useSocialShareStore = create<SocialShareState>((set) => ({
  settings: null,
  isLoading: false,

  fetchSettings: async () => {
    set({ isLoading: true });
    try {
      const { data } = await SECURE_API.get<SocialShareSettings>('socialShare');
      const settings: SocialShareSettings = {
        ...emptySocialShareSettings(),
        ...data,
        locales: {
          en: { ...emptyLocale(), ...data.locales?.en },
          hu: { ...emptyLocale(), ...data.locales?.hu },
          ro: { ...emptyLocale(), ...data.locales?.ro },
        },
        composedDocument: data.composedDocument || { blocks: [] },
      };
      set({ settings, isLoading: false });
      return settings;
    } catch {
      set({ settings: null, isLoading: false });
      return null;
    }
  },

  saveSettings: async (settings: SocialShareSettings) => {
    set({ isLoading: true });
    try {
      const { data } = await SECURE_API.put<SocialShareSettings>('socialShare', settings);
      const saved: SocialShareSettings = {
        ...emptySocialShareSettings(),
        ...data,
        locales: {
          en: { ...emptyLocale(), ...data.locales?.en },
          hu: { ...emptyLocale(), ...data.locales?.hu },
          ro: { ...emptyLocale(), ...data.locales?.ro },
        },
        composedDocument: data.composedDocument || { blocks: [] },
      };
      set({ settings: saved, isLoading: false });
      return saved;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
}));

export default useSocialShareStore;
